import React, { useEffect, useState, useRef } from "react";

// Helper: poll until the Google dropdown is ready
const applySelectValueWhenReady = (lang, attempts = 0) => {
  const MAX_ATTEMPTS = 20; // ~6 seconds
  const DELAY = 300;

  return new Promise((resolve) => {
    const trySet = () => {
      const select = document.querySelector(".goog-te-combo");
      if (select) {
        try {
          select.value = lang;
          const evt = new Event("change", { bubbles: true });
          select.dispatchEvent(evt);
          resolve(true);
          return;
        } catch (e) {}
      }

      if (attempts < MAX_ATTEMPTS) {
        setTimeout(() => {
          applySelectValueWhenReady(lang, attempts + 1).then(resolve);
        }, DELAY);
      } else {
        resolve(false);
      }
    };

    trySet();
  });
};

const applySavedLanguage = async (lang) => {
  if (!lang) return;
  await applySelectValueWhenReady(lang);
  localStorage.setItem("lang", lang);
};

const GoogleTranslate = () => {
  const savedLang =
    typeof window !== "undefined" ? localStorage.getItem("lang") : null;
  const [current, setCurrent] = useState(savedLang || "en");
  const [isOpen, setIsOpen] = useState(false);
  const popupRef = useRef();

  useEffect(() => {
    // Hide default Google UI
    const style = document.createElement("style");
    style.innerHTML = `
      .goog-te-banner-frame.skiptranslate { display:none !important; }
      body { top:0px !important; }
      .goog-te-combo, .goog-te-gadget { display:none !important; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    // Ensure savedLang always exists
    if (!savedLang) localStorage.setItem("lang", "en");

    // Google callback
    window.googleTranslateElementInit = () => {
      try {
        new window.google.translate.TranslateElement(
          { pageLanguage: "en", autoDisplay: false },
          "google_translate_element"
        );
      } catch {}
      const langToApply = localStorage.getItem("lang") || "en";
      applySavedLanguage(langToApply);
    };

    // Add translate script
    if (!document.querySelector("#google-translate-script")) {
      const addScript = document.createElement("script");
      addScript.id = "google-translate-script";
      addScript.src =
        "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      addScript.async = true;
      document.body.appendChild(addScript);
    } else if (
      window.google &&
      window.google.translate &&
      typeof window.googleTranslateElementInit === "function"
    ) {
      setTimeout(() => window.googleTranslateElementInit(), 300);
    }

    const handleOutsideClick = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
    // eslint-disable-next-line
  }, []);

  const handleChange = async (lang) => {
    await applySelectValueWhenReady(lang);
    setCurrent(lang);
    localStorage.setItem("lang", lang);
    setIsOpen(false);
  };

  const getLanguageText = (lang) => {
    switch (lang) {
      case "hi":
        return { flag: "/assets/indian-flag.png", name: "Hindi" };
      default:
        return { flag: "/assets/us-flag.png", name: "English" };
    }
  };

  const currentLang = getLanguageText(current);

  return (
    <div>
      {/* Hidden Google container */}
      <div id="google_translate_element" style={{ display: "none" }}></div>

      {/* Visible selector */}
      <div
        className="dropdown-select-container"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        <div className="dropdown-selected">
          <img src={currentLang.flag} alt={currentLang.name} />
          {currentLang.name}
        </div>
      </div>

      {isOpen && (
        <div className="popup-overlay">
          <div className="popup-content" ref={popupRef}>
            <h3>Select Language</h3>
            <div className="dropdown-select" onClick={() => handleChange("en")}>
              <img src="/assets/us-flag.png" alt="English" />
              English
            </div>
            <div className="dropdown-select" onClick={() => handleChange("hi")}>
              <img src="/assets/indian-flag.png" alt="Hindi" />
              Hindi
            </div>
          </div>
        </div>
      )}

      <style>{`
        .dropdown-select-container {
          display:inline-flex;align-items:center;gap:8px;
          padding:6px 12px;background:var(--white-color);
          border-radius:8px;box-shadow:0 0 6px rgba(0,0,0,0.1);
          cursor:pointer;color:var(--black-color);
        }
        .dropdown-selected{display:flex;align-items:center;gap:8px;color:black;}
        .dropdown-selected img{width:18px;height:18px;}
        .popup-overlay{
          position:fixed;top:0;left:0;width:100vw;height:100vh;
          padding-top:60px;padding-right:20px;display:flex;justify-content:end;align-items:start;
          z-index:10000;color:black;
        }
        .popup-content{
          background:var(--white-color);padding:10px;border-radius:12px;
          text-align:center;min-width:220px;box-shadow:0 4px 20px rgba(0,0,0,0.2);color:black;
        }
        .popup-content h3{margin-bottom:15px;font-size:16px;color:black;}
        .dropdown-select{
          display:flex;align-items:center;gap:10px;padding:8px 12px;margin:5px 0;
          color:black;border-radius:6px;cursor:pointer;transition:background 0.2s;
        }
        .dropdown-select:hover{background:rgba(0,0,0,0.05);}
        .dropdown-select img{width:18px;height:18px;}
        @media (max-width:600px){
          .dropdown-select-container{padding:5px 10px;font-size:12px;}
          .dropdown-select img,.dropdown-selected img{width:16px;height:16px;}
          .popup-content{width:80%;padding:15px 20px;}
        }
      `}</style>
    </div>
  );
};

export default GoogleTranslate;
